/**
 * LayoutPresetsMenu - Dropdown menu for quick layout preset switching
 *
 * BYK-169: Panel layout presets
 */

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUIStore, type LayoutPreset } from "@/stores/uiStore";
import { formatShortcut } from "@/hooks/useKeyboardShortcuts";
import { cn } from "@/lib/utils";

// Preset definitions with icons and metadata
const PRESET_CONFIG: Record<
  LayoutPreset,
  {
    label: string;
    description: string;
    shortcut: string[];
    icon: React.ReactNode;
  }
> = {
  "full-canvas": {
    label: "Full Canvas",
    description: "Hide all sidebars",
    shortcut: ["meta", "shift", "1"],
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
      </svg>
    ),
  },
  "design-mode": {
    label: "Design Mode",
    description: "Left sidebar open",
    shortcut: ["meta", "shift", "2"],
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="6" height="18" rx="1" strokeWidth={2} fill="currentColor" fillOpacity={0.2} />
        <rect x="11" y="3" width="10" height="18" rx="1" strokeWidth={2} />
      </svg>
    ),
  },
  "properties-mode": {
    label: "Properties Mode",
    description: "Right sidebar open",
    shortcut: ["meta", "shift", "3"],
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="10" height="18" rx="1" strokeWidth={2} />
        <rect x="15" y="3" width="6" height="18" rx="1" strokeWidth={2} fill="currentColor" fillOpacity={0.2} />
      </svg>
    ),
  },
  "split-view": {
    label: "Split View",
    description: "Both sidebars open",
    shortcut: ["meta", "shift", "4"],
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="5" height="18" rx="1" strokeWidth={2} fill="currentColor" fillOpacity={0.2} />
        <rect x="9" y="3" width="6" height="18" rx="1" strokeWidth={2} />
        <rect x="16" y="3" width="5" height="18" rx="1" strokeWidth={2} fill="currentColor" fillOpacity={0.2} />
      </svg>
    ),
  },
  "ai-focus": {
    label: "AI Focus",
    description: "Expanded AI panel",
    shortcut: ["meta", "shift", "5"],
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="3" width="8" height="18" rx="1" strokeWidth={2} />
        <rect x="13" y="3" width="8" height="18" rx="1" strokeWidth={2} fill="currentColor" fillOpacity={0.2} />
        <path d="M17 10l-2 2 2 2" strokeWidth={2} strokeLinecap="round" />
      </svg>
    ),
  },
};

const PRESET_ORDER: LayoutPreset[] = [
  "full-canvas",
  "design-mode",
  "properties-mode",
  "split-view",
  "ai-focus",
];

export function LayoutPresetsMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { activePreset, applyPreset } = useUIStore();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleSelectPreset = (preset: LayoutPreset) => {
    applyPreset(preset);
    setIsOpen(false);
  };

  const currentPreset = activePreset || "split-view";
  const currentConfig = PRESET_CONFIG[currentPreset];

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors",
          isOpen
            ? "bg-gray-200 text-gray-900"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        )}
        title="Layout presets"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {currentConfig.icon}
        <svg
          className={cn(
            "h-3 w-3 transition-transform",
            isOpen && "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            role="menu"
          >
            <div className="px-3 py-2 text-xs font-medium text-gray-500">
              Layout Presets
            </div>

            {PRESET_ORDER.map((preset) => {
              const config = PRESET_CONFIG[preset];
              const isActive = currentPreset === preset;

              return (
                <button
                  key={preset}
                  onClick={() => handleSelectPreset(preset)}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                  role="menuitem"
                >
                  <div className="flex items-center gap-2.5">
                    <span className={isActive ? "text-blue-600" : "text-gray-400"}>
                      {config.icon}
                    </span>
                    <div className="text-left">
                      <div className="font-medium">{config.label}</div>
                      <div className="text-xs text-gray-500">
                        {config.description}
                      </div>
                    </div>
                  </div>
                  <kbd className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
                    {formatShortcut(config.shortcut)}
                  </kbd>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LayoutPresetsMenu;
